import { Directive, ElementRef, HostListener, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Formats a phone input as the dashed `555-123-4567` US (10-digit) format,
 * both as the user types and on every programmatic write to the control
 * (`patchValue`/`setValue`, an `[initialData]` edit-flow re-population, etc.)
 * — implemented as a `ControlValueAccessor` so Angular calls `writeValue()`
 * directly instead of relying on a `valueChanges` subscription, which can
 * miss a synchronous write in the host component's own constructor/`ngOnInit`
 * (runs before this directive subscribes) or one made with
 * `{ emitEvent: false }` (which a `valueChanges` subscription never sees at
 * all). `writeValue()` only updates the displayed value; the control itself
 * keeps whatever value was written to it (masked, via the typing path below,
 * or raw, via a programmatic write) — the API accepts both dashed and raw.
 *
 * Two bugs fixed (#1287):
 *  1. The previous mask wrote `(555)-123-4567` which did NOT match the form's
 *     `Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)`, so a correctly-typed number
 *     was rejected. The mask now emits the dashed format the validator expects.
 *  2. The mask only mutated `input.value` (the DOM) and never told Angular, so
 *     the FormControl kept the raw keystrokes and the pattern validator ran
 *     against an unmasked value. We now call the registered CVA `onChange`
 *     with the masked value so the reactive FormControl reads it.
 */
/**
 * Progressive `NNN-NNN-NNNN` US mask used by `abxPhoneMask` and by
 * `bodyanalytics-field-input[mask="phone"]` so every phone field in every
 * portal formats identically.
 */
export function formatUsPhoneMask(raw: string): string {
  let digits = raw.replace(/\D+/g, '');
  // A pasted E.164 / "1-###-###-####" number carries the US country code:
  // drop it so the mask yields the 10-digit national form the validator and
  // the API expect (instead of "194-520-1123" — the wrong ten digits).
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  const cleaned = digits.slice(0, 10);
  const match = cleaned.match(/^(\d{1,3})(\d{1,3})?(\d{1,4})?$/);

  return match ? `${match[1]}${match[2] ? '-' + match[2] : ''}${match[3] ? '-' + match[3] : ''}` : '';
}

@Directive({
  standalone: true,
  selector: 'input[abxPhoneMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneMaskDirective),
      multi: true,
    },
  ],
})
export class PhoneMaskDirective implements ControlValueAccessor {
  private readonly elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.elementRef.nativeElement.value = typeof value === 'string' ? this.format(value) : '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.format(input.value);

    if (input.value !== formatted) {
      input.value = formatted;
    }
    // Tell Angular's reactive forms the value changed so the FormControl (and
    // its pattern validator) sees the masked, dashed value — not the raw
    // keystrokes (#1287).
    this.onChange(formatted);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  private format(raw: string): string {
    return formatUsPhoneMask(raw);
  }
}
